import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, LayoutGrid } from "lucide-react";
import { TreatmentPhotoGrid } from "@/components/menu/TreatmentPhotoGrid";

/**
 * Mobile bottom-sheet variant of the Treatments mega-menu.
 * Slides up from the bottom on mobile when the user taps "Treatments".
 * Auto-closes when any link is tapped (the click then routes normally).
 */
export default function MobileTreatmentsSheet({ open, onClose, language = "en" }) {
  const lang = language === "fr" ? "fr" : "en";

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10002] md:hidden"
      data-testid="mobile-treatments-sheet"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close menu"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        data-testid="mobile-treatments-backdrop"
      />

      {/* Sheet */}
      <div
        className="absolute inset-x-0 bottom-0 max-h-[88vh] bg-[#fafaf7] rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-300"
        role="dialog"
        aria-modal="true"
        aria-label="Treatments menu"
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-2 pb-1 shrink-0">
          <div className="h-1.5 w-12 rounded-full bg-zinc-300" aria-hidden="true" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-zinc-200 shrink-0">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-medium">
              {lang === "fr" ? "Catalogue" : "Catalogue"}
            </p>
            <h2 className="text-xl font-light tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {lang === "fr" ? "Traitements" : "Treatments"}
            </h2>
            {/* Clinic legend — Feb 2026 · Karine ask so clients know OTT / KAN / CP. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[9px] text-zinc-500" data-testid="mobile-clinic-legend">
              <span className="inline-flex items-center font-bold uppercase tracking-wider rounded-full border text-[8px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-700 border-emerald-500/40">OTT</span>
              <span className="opacity-60">Ottawa</span>
              <span className="inline-flex items-center font-bold uppercase tracking-wider rounded-full border text-[8px] px-1.5 py-0.5 bg-amber-500/15 text-amber-700 border-amber-500/40 ml-1">KAN</span>
              <span className="opacity-60">Kanata</span>
              <span className="inline-flex items-center font-bold uppercase tracking-wider rounded-full border text-[8px] px-1.5 py-0.5 bg-rose-500/15 text-rose-700 border-rose-500/40 ml-1">CP</span>
              <span className="opacity-60">Carleton Pl.</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-zinc-100 transition-colors shrink-0"
            aria-label="Close"
            data-testid="mobile-treatments-close"
          >
            <X className="w-5 h-5 text-zinc-700" />
          </button>
        </div>

        {/* Scrollable content — photo-card grid (Oct 10 2026 · Karine ask) */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          <TreatmentPhotoGrid lang={lang} onNavigate={onClose} cols="grid-cols-2 sm:grid-cols-3" testIdPrefix="mobile-cat" />
        </div>

        {/* Bottom CTA */}
        <div className="shrink-0 px-5 py-3 bg-gradient-to-r from-amber-50/60 via-amber-50/40 to-zinc-50 border-t border-zinc-100">
          <Link
            to="/treatments"
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-zinc-900 text-zinc-50 text-sm font-medium active:bg-zinc-800"
            data-testid="mobile-view-all-link"
          >
            <LayoutGrid className="w-4 h-4" />
            {lang === "fr" ? "Voir tous les traitements" : "View All Treatments"}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
